import WarningIcon from '@mui/icons-material/Warning'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'

import { isPricingEnabled } from '../../config/environment'
import { getTierById } from '../../config/subscriptionTiers'
import {
  getCapacityInfo,
  CapacityInfo,
  createPortalSession,
  getSubscription
} from '../../services/subscriptionService'
import { PricingModal } from '../pricing'

interface OfficeSubscriptionStatusProps {
  officeId: string
  refreshTrigger?: number // Optional prop to trigger refresh
}

/**
 * OfficeSubscriptionStatus Component
 *
 * Displays the current subscription status and capacity usage for an office.
 * Shows:
 * - Current tier name with badge
 * - User count as "X / Y users"
 * - Remaining slots
 * - Progress bar for capacity usage with color coding:
 *   - Green: < 70% capacity
 *   - Yellow: 70-90% capacity
 *   - Red: > 90% capacity
 * - Warning icon when at or near capacity (90% or above)
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5
 *
 * @param officeId - The office ID to display subscription status for
 */
export const OfficeSubscriptionStatus = ({
  officeId,
  refreshTrigger
}: OfficeSubscriptionStatusProps) => {
  const [capacityInfo, setCapacityInfo] = useState<CapacityInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  const [isLoadingPortal, setIsLoadingPortal] = useState(false)
  const [pricingModalOpen, setPricingModalOpen] = useState(false)

  useEffect(() => {
    const fetchCapacityInfo = async () => {
      try {
        setLoading(true)
        setError(null)
        const info = await getCapacityInfo(officeId)
        setCapacityInfo(info)

        // Check if office has an active subscription (not free tier)
        const subscription = await getSubscription(officeId)
        setHasActiveSubscription(
          subscription !== null &&
            subscription.status === 'active' &&
            subscription.tier !== 'free'
        )
      } catch (err) {
        console.error('Error fetching capacity info:', err)
        setError('Failed to load subscription status')
      } finally {
        setLoading(false)
      }
    }

    fetchCapacityInfo()
  }, [officeId, refreshTrigger])

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant='body2' color='text.secondary'>
            Loading subscription status...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error || !capacityInfo) {
    return (
      <Card>
        <CardContent>
          <Typography variant='body2' color='error'>
            {error || 'Unable to load subscription status'}
          </Typography>
        </CardContent>
      </Card>
    )
  }

  const { currentUsers, capacity, tier, remaining, isOverLimit } = capacityInfo
  const tierConfig = getTierById(tier)
  const usagePercentage = (currentUsers / capacity) * 100
  const isNearCapacity = usagePercentage >= 90
  const isHighestTier = tier === 'large'

  // Determine progress bar color based on usage
  const getProgressColor = (): 'success' | 'warning' | 'error' => {
    if (isOverLimit) return 'error'
    if (usagePercentage >= 90) return 'error'
    if (usagePercentage >= 70) return 'warning'
    return 'success'
  }

  const handleUpgrade = () => {
    setPricingModalOpen(true)
  }

  const handleManageBilling = async () => {
    try {
      setIsLoadingPortal(true)
      const portalUrl = await createPortalSession(officeId)
      // Redirect to Stripe Customer Portal
      window.location.href = portalUrl
    } catch (err) {
      console.error('Error opening billing portal:', err)
      setError('Failed to open billing portal. Please try again.')
      setIsLoadingPortal(false)
    }
  }

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          <Typography variant='h6' component='div'>
            Subscription Status
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isOverLimit && (
              <Chip
                label='Over Limit'
                color='error'
                size='small'
                data-testid='over-limit-badge'
              />
            )}
            <Chip
              label={tierConfig.name}
              color={tier === 'free' ? 'default' : 'primary'}
              size='small'
            />
          </Box>
        </Box>

        {isOverLimit && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: 'error.light',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'error.main'
            }}
          >
            <Typography variant='body2' color='error.dark' fontWeight={600}>
              Subscription Lapsed - Over Limit
            </Typography>
            <Typography variant='body2' color='error.dark' sx={{ mt: 0.5 }}>
              This office has more than the Free tier limit of 5 users. Existing
              users can continue, but new users cannot be added until the count
              drops to 5 or below, or you upgrade your subscription.
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              User Capacity
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {(isNearCapacity || isOverLimit) && (
                <WarningIcon
                  color='error'
                  sx={{ fontSize: 18 }}
                  data-testid='capacity-warning-icon'
                />
              )}
              <Typography variant='body2' fontWeight={600}>
                {currentUsers} / {capacity} users
              </Typography>
            </Box>
          </Box>

          <LinearProgress
            variant='determinate'
            value={Math.min(usagePercentage, 100)}
            color={getProgressColor()}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>

        <Typography variant='body2' color='text.secondary'>
          {remaining > 0 ? (
            <>
              <strong>{remaining}</strong> {remaining === 1 ? 'slot' : 'slots'}{' '}
              remaining
            </>
          ) : (
            <strong>No slots remaining</strong>
          )}
        </Typography>

        {/* Action Buttons */}
        {isPricingEnabled() && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {!isHighestTier && (
              <Button
                variant='contained'
                color='primary'
                onClick={handleUpgrade}
                fullWidth
                data-testid='upgrade-button'
              >
                Upgrade
              </Button>
            )}
            {hasActiveSubscription && (
              <Button
                variant='outlined'
                color='primary'
                onClick={handleManageBilling}
                disabled={isLoadingPortal}
                fullWidth
                data-testid='manage-billing-button'
              >
                {isLoadingPortal ? 'Loading...' : 'Manage Billing'}
              </Button>
            )}
          </Box>
        )}
      </CardContent>

      <PricingModal
        open={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
        preSelectedOfficeId={officeId}
      />
    </Card>
  )
}

export default OfficeSubscriptionStatus
